import React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { novaYellow, NovaThemeName } from './novaTheme';

// The five clips from the motion handover (NovaIndicatorMotionSpec): each is a
// self-contained looping SMIL animation exported straight from motion design,
// living under public/nova/mark/{amber|acid}/<state>.svg. 'hello' is authored to
// loop but is meant to play once per hover — callers drive that by only setting
// state="hello" for the clip's own ~1.6s duration (see NovaAvatar), then falling
// back to a resting mark; NovaMark itself just renders whatever state it's given.
export type NovaMarkState = 'hello' | 'breathing' | 'leanin' | 'investigating' | 'spin';

export const answerMarkState = ({ streaming, leanIn }: { streaming?: boolean; leanIn?: boolean }): NovaMarkState =>
  streaming ? 'spin' : leanIn ? 'leanin' : 'breathing';

export type NovaMarkColourway = 'amber' | 'acid';

// Amber on light, Acid on dark — colourway follows the surface the mark sits on,
// never the indicator's state (spec note 08).
const THEME_COLOURWAY: Record<NovaThemeName, NovaMarkColourway> = { light: 'amber', dark: 'acid' };

interface NovaMarkProps {
  state?: NovaMarkState;
  /** Site theme — picks amber/acid automatically unless `colourway` overrides it. */
  theme?: NovaThemeName;
  /** Explicit colourway for a mark on a fixed-color surface (e.g. the always-dark launcher roundel), regardless of site theme. */
  colourway?: NovaMarkColourway;
  size?: number;
  className?: string;
  /** Static fallback colour (resting gold in light mode, lightest yellow on dark) instead of a clip. */
  staticColor?: string;
  /** Set false to render a plain, motionless mark — no clip, just the brand glyph. */
  animated?: boolean;
}

// Exact geometry from the brand spec export (NovaMotionLanguage_RS_001.html):
// four independently-authored arms — not one stroke repeated via rotation —
// each already closed with cubic-bezier corners for the soft fold the spec
// calls for, so no corner-rounding pass is needed on top of these. Used for the
// plain static glyph (animated=false, prefers-reduced-motion, or a load error).
const ARM_PATHS = [
  'M44.5,27.9l76.32,26.25c2.15.74,4.4,1.1,6.66,1.1s4.51-.37,6.65-1.11l76.32-26.25c1.33-.46,2.31-1.55,2.62-2.92.32-1.37-.08-2.78-1.08-3.79l-15.9-15.9c-4.86-4.86-12.19-6.55-18.69-4.32l-49.93,17.18L77.55.97c-6.5-2.23-13.83-.54-18.69,4.32l-15.9,15.9c-.99.99-1.4,2.4-1.09,3.77.31,1.37,1.3,2.47,2.63,2.94Z',
  'M236.82,127.49l17.18-49.93c2.24-6.5.54-13.83-4.32-18.69l-15.9-15.9c-.99-.99-2.4-1.4-3.77-1.09-1.37.31-2.47,1.3-2.94,2.64l-26.25,76.31c-1.47,4.29-1.47,9.02,0,13.3l26.25,76.32c.46,1.33,1.55,2.31,2.92,2.62.31.07.62.11.93.11,1.06,0,2.08-.41,2.86-1.19l15.9-15.9c4.86-4.86,6.55-12.19,4.32-18.69l-17.18-49.93Z',
  'M54.15,120.83L27.9,44.51c-.46-1.33-1.55-2.31-2.92-2.62-1.37-.32-2.78.08-3.79,1.08l-15.9,15.9C.43,63.72-1.26,71.06.97,77.56l17.18,49.93L.97,177.41c-2.23,6.5-.54,13.83,4.32,18.69l15.9,15.9c.77.77,1.8,1.19,2.86,1.19.3,0,.61-.03.91-.1,1.37-.31,2.47-1.3,2.94-2.64l26.25-76.31c1.47-4.29,1.47-9.02,0-13.3Z',
  'M210.47,227.08l-76.32-26.25c-4.29-1.47-9.02-1.47-13.3,0l-76.32,26.25c-1.33.46-2.31,1.55-2.62,2.92s.08,2.78,1.08,3.79l15.9,15.9c4.86,4.86,12.19,6.55,18.69,4.32l49.93-17.18,49.93,17.18c1.9.65,3.88.97,5.84.97,4.74,0,9.42-1.85,12.85-5.29l15.9-15.9c.99-.99,1.4-2.4,1.09-3.77-.31-1.37-1.3-2.47-2.63-2.94Z',
];

const StaticGlyph: React.FC<{ size: number; className?: string; fill: string }> = ({ size, className, fill }) => (
  <svg viewBox="0 0 254.97 254.97" width={size} height={size} className={className} aria-hidden="true">
    <g fill={fill}>
      {ARM_PATHS.map((d) => (
        <path key={d} d={d} />
      ))}
    </g>
  </svg>
);

const NovaMark: React.FC<NovaMarkProps> = ({
  state = 'breathing',
  theme,
  colourway,
  size = 64,
  className,
  staticColor,
  animated = true,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const fill = staticColor || novaYellow.rest;

  // Spec note 06: under prefers-reduced-motion, every state renders as the plain
  // static mark — no clip ever plays, progress is conveyed in text elsewhere.
  if (!animated || prefersReducedMotion) {
    return <StaticGlyph size={size} className={className} fill={fill} />;
  }

  const resolvedColourway = colourway || THEME_COLOURWAY[theme || 'light'];
  const src = `/nova/mark/${resolvedColourway}/${state}.svg`;

  return (
    <span
      className={className}
      style={{ position: 'relative', display: 'inline-block', width: size, height: size, flex: 'none' }}
    >
      {/* Spec note 04: 160ms opacity cross-fade between clips, never a hard cut —
          the outgoing clip fades out in place while the incoming one fades in. */}
      <AnimatePresence initial={false}>
        <motion.img
          key={`${resolvedColourway}-${state}`}
          src={src}
          width={size}
          height={size}
          alt=""
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16, ease: 'easeInOut' }}
          style={{ position: 'absolute', inset: 0, display: 'block' }}
        />
      </AnimatePresence>
    </span>
  );
};

export default NovaMark;
